import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { formatCurrency } from '@/lib/format';
import {
  CatalogValidationError,
  PriceBookChangedError,
  useApplyPriceAdjustment,
  usePreviewPriceAdjustment,
  type CatalogCategory,
  type PriceAdjustmentDirection,
  type PriceAdjustmentLine,
  type PriceAdjustmentMode,
  type PriceAdjustmentRequest,
} from '@/routes/pricebook/usePricebook';

/**
 * Bulk price adjustment across one category (M2-02) — preview, then apply.
 *
 * Two steps in one dialog. The first asks which category, percent or dollars,
 * up or down, and how much. The second shows every item's old and new price
 * and applies nothing until the office confirms. What is applied is exactly
 * what was shown: the preview's lines go back to the API as `expected`, and if
 * anybody changed the book in between, the API refuses and this dialog says
 * so and returns to a fresh preview rather than writing prices nobody saw.
 *
 * Changing any input after a preview discards it, so the Apply button can
 * never send numbers from one preview with inputs from another.
 */
export function PriceAdjustmentDialog({
  categories,
  initialCategoryId,
  onClose,
  onAnnounce,
}: {
  /** Active categories, in display order. */
  categories: CatalogCategory[];
  /** The list's current category filter, if it is one — the likely target. */
  initialCategoryId?: string;
  onClose: () => void;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const fieldId = useId();
  const preview = usePreviewPriceAdjustment();
  const apply = useApplyPriceAdjustment();

  const [categoryId, setCategoryId] = useState(
    initialCategoryId && categories.some((c) => c.id === initialCategoryId)
      ? initialCategoryId
      : (categories[0]?.id ?? ''),
  );
  const [mode, setMode] = useState<PriceAdjustmentMode>('Percent');
  const [direction, setDirection] = useState<PriceAdjustmentDirection>('Increase');
  const [amount, setAmount] = useState('');
  const [lines, setLines] = useState<PriceAdjustmentLine[] | null>(null);
  const [previewedWith, setPreviewedWith] = useState<PriceAdjustmentRequest | null>(null);
  const [error, setError] = useState('');

  const categoryLabel = categories.find((c) => c.id === categoryId)?.label ?? '';

  function discardPreview() {
    setLines(null);
    setPreviewedWith(null);
  }

  function runPreview() {
    setError('');
    const parsed = Number(amount.trim());
    if (amount.trim() === '' || !Number.isFinite(parsed) || parsed <= 0) {
      setError(t('pricebook.adjust.amountRequired'));
      return;
    }
    const request: PriceAdjustmentRequest = { categoryId, mode, direction, amount: parsed };
    preview.mutate(request, {
      onSuccess: (result) => {
        setLines(result);
        setPreviewedWith(request);
      },
      onError: (err) =>
        setError(
          err instanceof CatalogValidationError ? err.message : t('pricebook.adjust.previewFailed'),
        ),
    });
  }

  function runApply() {
    if (!lines || !previewedWith) {
      return;
    }
    setError('');
    apply.mutate(
      { adjustment: previewedWith, preview: lines },
      {
        onSuccess: (applied) => {
          // Lines the adjustment left where they were (a $0 item under a
          // percentage) are not "updated" — the API does not rewrite them,
          // and the Apply button did not count them either.
          const updated = applied.filter(
            (line) => Number(line.newPrice) !== Number(line.oldPrice),
          ).length;
          onAnnounce(t('pricebook.adjust.applied', { count: updated, category: categoryLabel }));
          onClose();
        },
        onError: (err) => {
          if (err instanceof PriceBookChangedError) {
            // The server's sentence, and back to the form: the old preview is
            // no longer true, and showing it beside an error would invite
            // somebody to read it as what just happened.
            discardPreview();
            setError(err.message);
            return;
          }
          setError(
            err instanceof CatalogValidationError ? err.message : t('pricebook.adjust.applyFailed'),
          );
        },
      },
    );
  }

  const changed = lines?.filter((line) => Number(line.newPrice) !== Number(line.oldPrice)) ?? [];
  const floored = lines?.filter((line) => line.flooredAtZero) ?? [];

  return (
    <Dialog
      titleId={titleId}
      title={t('pricebook.adjust.title')}
      onClose={onClose}
      testId="price-adjustment-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {lines ? (
            <Button onClick={runApply} disabled={apply.isPending || changed.length === 0}>
              {t('pricebook.adjust.apply', { count: changed.length })}
            </Button>
          ) : (
            <Button onClick={runPreview} disabled={preview.isPending || !categoryId}>
              {t('pricebook.adjust.preview')}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-3">
        {categories.length === 0 ? (
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {t('pricebook.adjust.noCategories')}
          </p>
        ) : (
          // ⚠️ Disabled while a request is in flight. Otherwise a change made
          // before the preview returns is discarded against a preview that
          // does not exist yet, and the answer then lands beside inputs it
          // was not computed from — Apply would send what the table shows,
          // but the boxes above it would say something else.
          <fieldset
            disabled={preview.isPending || apply.isPending}
            className="space-y-3"
            data-testid="price-adjustment-inputs"
          >
            <div>
              <label htmlFor={`${fieldId}-category`} className="text-sm font-medium">
                {t('pricebook.form.category')}
              </label>
              <select
                id={`${fieldId}-category`}
                className={FIELD_CLASS}
                value={categoryId}
                onChange={(event) => {
                  setCategoryId(event.target.value);
                  discardPreview();
                }}
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label htmlFor={`${fieldId}-direction`} className="text-sm font-medium">
                  {t('pricebook.adjust.direction')}
                </label>
                <select
                  id={`${fieldId}-direction`}
                  className={FIELD_CLASS}
                  value={direction}
                  onChange={(event) => {
                    setDirection(event.target.value as PriceAdjustmentDirection);
                    discardPreview();
                  }}
                >
                  <option value="Increase">{t('pricebook.adjust.increase')}</option>
                  <option value="Decrease">{t('pricebook.adjust.decrease')}</option>
                </select>
              </div>

              <div>
                <label htmlFor={`${fieldId}-mode`} className="text-sm font-medium">
                  {t('pricebook.adjust.mode')}
                </label>
                <select
                  id={`${fieldId}-mode`}
                  className={FIELD_CLASS}
                  value={mode}
                  onChange={(event) => {
                    setMode(event.target.value as PriceAdjustmentMode);
                    discardPreview();
                  }}
                >
                  <option value="Percent">{t('pricebook.adjust.percent')}</option>
                  <option value="Fixed">{t('pricebook.adjust.fixed')}</option>
                </select>
              </div>

              <div>
                <label htmlFor={`${fieldId}-amount`} className="text-sm font-medium">
                  {mode === 'Percent'
                    ? t('pricebook.adjust.amountPercent')
                    : t('pricebook.adjust.amountDollars')}
                </label>
                <input
                  id={`${fieldId}-amount`}
                  type="number"
                  inputMode="decimal"
                  min={0.01}
                  step="0.01"
                  className={FIELD_CLASS}
                  value={amount}
                  onChange={(event) => {
                    setAmount(event.target.value);
                    discardPreview();
                  }}
                />
              </div>
            </div>
          </fieldset>
        )}

        {error ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {error}
          </p>
        ) : null}

        {lines ? (
          <div className="space-y-2" data-testid="price-adjustment-preview">
            <h3 className="text-sm font-semibold">
              {t('pricebook.adjust.previewHeading', { category: categoryLabel })}
            </h3>
            {lines.length === 0 ? (
              <p className="text-sm text-[var(--color-muted-foreground)]">
                {t('pricebook.adjust.emptyCategory')}
              </p>
            ) : (
              <>
                {floored.length > 0 ? (
                  // Not an error — the API floors and says so per line — but
                  // a $40 cut to a $25 part is rarely what was meant, and this
                  // is the last screen before it is written.
                  <p className="text-sm font-medium text-[var(--color-destructive)]">
                    {t('pricebook.adjust.flooredWarning', { count: floored.length })}
                  </p>
                ) : null}
                <div
                  className="max-h-72 overflow-y-auto rounded-md border"
                  tabIndex={0}
                  role="region"
                  aria-label={t('pricebook.adjust.previewHeading', { category: categoryLabel })}
                >
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th scope="col" className="px-3 py-2 font-medium">
                          {t('pricebook.columns.name')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">
                          {t('pricebook.adjust.oldPrice')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">
                          {t('pricebook.adjust.newPrice')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <tr
                          key={line.id}
                          className="border-b last:border-b-0"
                          data-testid="price-adjustment-line"
                        >
                          <td className="px-3 py-2">
                            {line.name}
                            {line.flooredAtZero ? (
                              <span className="ml-2 text-xs text-[var(--color-destructive)]">
                                {t('pricebook.adjust.flooredChip')}
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatCurrency(Number(line.oldPrice))}
                          </td>
                          <td className="px-3 py-2 text-right font-medium tabular-nums">
                            {formatCurrency(Number(line.newPrice))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
